import type { TFunction } from 'i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import 'dayjs/locale/en-gb'
import type {
  ChatwootAssignee,
  ChatwootConversation,
  ChatwootInboxOption,
  ChatwootMessage,
} from './types'
import { formatCompanyLabel } from './companyFilter'
import { isForeignAgentName } from './contactIdentity'

// Compact "time ago" for the conversation list, matching Chatwoot/Intercom:
// "now", "5m", "2h", "3d", then a short calendar date once it's over a week
// old. `nowMs` is injectable so the formatter stays pure and testable.
//
// `withClockTime` adds the time of day to that calendar date. Only the
// message list asks for it: in a thread, "12 Aug" is not enough to tell two
// messages from the same day apart, and reconstructing a payroll conversation
// means knowing when each side answered. The conversation list rows and the
// customer bubble stay on the short form — one line per conversation, in a
// narrow column, where the extra five characters cost more than they say.
export function formatCompactTimestamp(
  timestampSeconds: number | undefined | null,
  options: {
    nowLabel: string
    locale?: 'de' | 'en'
    nowMs?: number
    withClockTime?: boolean
  }
): string {
  if (!timestampSeconds) {
    return ''
  }

  const thenMs = timestampSeconds * 1000
  const nowMs = options.nowMs ?? Date.now()
  const diffSeconds = Math.max(0, Math.round((nowMs - thenMs) / 1000))

  if (diffSeconds < 60) {
    return options.nowLabel
  }
  const diffMinutes = Math.floor(diffSeconds / 60)
  if (diffMinutes < 60) {
    return `${diffMinutes}m`
  }
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) {
    return `${diffHours}h`
  }
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) {
    return `${diffDays}d`
  }

  // Both branches pin an instance locale. `dayjs()` alone inherits the GLOBAL
  // locale, which localeController drives from the app language — so on a German
  // session the English branch rendered German month names ("8 Juni" for an
  // en-DE operator). This formatter is documented as pure; leave it that way.
  const isGerman = options.locale === 'de'
  const date = dayjs(thenMs).locale(isGerman ? 'de' : 'en-gb')
  if (!options.withClockTime) {
    return date.format('D MMM')
  }
  // German operators read dates numerically ("15.07. 14:22"); English keeps the
  // short month name the rest of the app already uses.
  return date.format(isGerman ? 'DD.MM. HH:mm' : 'D MMM HH:mm')
}

// Chatwoot labels contacts that reach us without a real name (e.g. an email
// with no display name in the "From" header, or an unidentified web visitor)
// with a generic placeholder like "Anonymous". When that happens the email's
// local part (the bit before "@") is a more useful label for operators, so we
// prefer it whenever there is an email to fall back to.
const ANONYMOUS_NAME_PATTERN = /^anonymous$/i

export function getEmailLocalPart(email?: string): string | undefined {
  const localPart = email?.split('@')[0]?.trim()
  return localPart || undefined
}

// Pick the inbox to pre-select for the operator starting a new conversation.
// Each operator has their own Channel::Email inbox addressed as
// "<login-local-part>@support.cleverlohn.de" (the same local part their login
// email carries, e.g. "erika.mustermann@cleverlohn.de" →
// "erika.mustermann@support.cleverlohn.de"), so we match the operator's login
// email local part against each inbox's own address. Falling back to the first
// inbox keeps the previous behaviour when the operator has no matching email
// inbox (e.g. chat-only accounts) or the address can't be resolved.
export function pickDefaultInbox(
  inboxes: ChatwootInboxOption[],
  operatorEmail?: string | null
): ChatwootInboxOption | undefined {
  if (inboxes.length === 0) {
    return undefined
  }
  const operatorLocalPart = getEmailLocalPart(
    operatorEmail?.trim().toLowerCase()
  )
  if (operatorLocalPart) {
    const ownInbox = inboxes.find(
      (inbox) =>
        getEmailLocalPart(inbox.emailAddress?.trim().toLowerCase()) ===
        operatorLocalPart
    )
    if (ownInbox) {
      return ownInbox
    }
  }
  return inboxes[0]
}

function isAnonymousName(name: string, email?: string): boolean {
  const normalizedName = name.trim().toLowerCase()
  const normalizedEmail = email?.trim().toLowerCase()
  return (
    ANONYMOUS_NAME_PATTERN.test(name) ||
    (!!normalizedEmail && normalizedName === normalizedEmail)
  )
}

// Resolve the name to show for a message/conversation author. Falls back to the
// email's local part when the name is missing or an anonymous placeholder.
export function getAuthorDisplayName(
  name?: string,
  email?: string
): string | undefined {
  const trimmedName = name?.trim()
  if (trimmedName && !isAnonymousName(trimmedName, email)) {
    return trimmedName
  }
  return getEmailLocalPart(email) || trimmedName || undefined
}

// A bare numeric string (a Chatwoot contact/agent id) is never a real display
// name, so it must not surface as the conversation title.
function isUsableName(value: string | undefined): value is string {
  return !!value && !/^\d+$/.test(value.trim())
}

export function getConversationTitle(
  conversation: ChatwootConversation | undefined,
  t: TFunction,
  // The account's agents, used to spot a contact name that is really one of our
  // own people. Optional: without the directory the title behaves
  // exactly as before.
  agents: ChatwootAssignee[] = []
): string {
  if (!conversation) {
    return t('frontend:chatwoot.field.messages')
  }

  // Prefer the contact (the customer who initiated the conversation) over
  // message authors, so the title names the initiator rather than an operator
  // who happened to author the first/last message. Resolve a single actor and
  // read both name and email from it, so the title can never pair one actor's
  // name with another's email. Skip actors with neither field (e.g. an empty
  // primary_contact) so a later actor that does carry identity still wins.
  const preferredContact = [
    conversation.primary_contact,
    conversation.initial_message_author,
    conversation.last_message_author,
  ].find((actor) => actor?.name || actor?.email)
  const contactName = preferredContact?.name
  const contactEmail = preferredContact?.email

  const displayName = getAuthorDisplayName(contactName, contactEmail)
  // getAuthorDisplayName can still return a bare id when that's all Chatwoot
  // stored; treat that as "no name" so we fall through to the company/number.
  // A name belonging to one of our agents rather than to this address is
  // dropped the same way, leaving the address to speak for itself.
  const usableName =
    isUsableName(displayName) &&
    !isForeignAgentName(contactName, contactEmail, agents)
      ? displayName
      : undefined

  if (usableName && contactEmail && usableName !== contactEmail) {
    return `${usableName} (${contactEmail})`
  }
  if (usableName) {
    return usableName
  }
  if (contactEmail) {
    return contactEmail
  }

  // Override Chatwoot's default (numeric) title with the company name + client
  // number when we resolved it, so operators see the company instead of an id.
  const companyLabel = conversation.company
    ? formatCompanyLabel(conversation.company)
    : null
  if (companyLabel) {
    return companyLabel
  }

  return t('frontend:chatwoot.field.conversationNumber', {
    0: conversation.id,
  })
}

// Chatwoot's own fallback subject for mail threads that were created without a
// real one: "[#<display_id>] New messages on this conversation" (a replying
// mail client may prefix "Re: " one or more times). It is internal boilerplate,
// never something a human typed, so it must not surface as a subject in the UI.
// Mirrors the backend `isBoilerplateMailSubject`
// (functions/src/functions/chatwoot/mailSubject.ts).
const BOILERPLATE_MAIL_SUBJECT =
  /^(?:re:\s*)*\[#\d+\]\s*new messages on this conversation$/i

// The human-authored email subject to display for a conversation, or undefined
// when there is none worth showing. Only mail-channel threads carry a subject;
// widget/chat threads have none. Chatwoot's internal boilerplate fallback is
// treated as "no subject" so it never leaks into the list row or header.
export function getConversationSubject(
  conversation: ChatwootConversation | undefined
): string | undefined {
  const trimmed = conversation?.subject?.trim()
  if (!trimmed || BOILERPLATE_MAIL_SUBJECT.test(trimmed)) {
    return undefined
  }
  return trimmed
}

// Last-message preview for a conversation card (used by the advanced-search
// result row). Returns "Author: text" when the conversation carries embedded
// message data (search endpoint), just the text when the author is absent, or
// null when no message body is available (filter endpoint omits message bodies).
export function getConversationSnippet(
  conversation: ChatwootConversation & { last_message?: string }
): string | null {
  const author = conversation.last_message_author
  const authorName = author?.name ?? author?.email ?? ''
  const lastMsg = conversation.last_message ?? ''
  if (authorName && lastMsg) return `${authorName}: ${lastMsg}`
  if (lastMsg) return lastMsg
  return null
}

// Cleo drafts a reply only when the customer is actually waiting on one
// (Intercom parity): the newest conversational message — skipping activity,
// private notes, and senderless system templates — must be incoming. Returns
// that message's id, which keys the suggestion card so a new customer message
// yields a fresh draft; null means no draft is wanted (the operator/bot spoke
// last, or the thread has nothing conversational yet).
//
// A resolved or snoozed thread wants no draft either, whatever the messages say:
// resolving is an activity message, so without the state a thread closed without
// a reply reads as unanswered forever. Omit the state to skip the
// check.
export function getAwaitingReplyMessageId(
  messages: ChatwootMessage[],
  conversationState?: string | null
): string | null {
  if (conversationState === 'resolved' || conversationState === 'snoozed') {
    return null
  }
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index]
    if (
      message.private ||
      message.message_type === 'activity' ||
      message.senderless_system_template
    ) {
      continue
    }
    return message.message_type === 'incoming' ? message.id : null
  }
  return null
}
